import { useEffect, useMemo, useState } from 'react';
import type { StepContentProps } from '~/ui-rocco/multi-step-form';
import { Input } from '@rocco/components/input';
import { StepContent } from '../StepContent';
import type { CurrentMemberFragment } from 'generated/graphql';
import {
    getHdeFieldLabelClassName,
    getHdeInputClassName,
    getHdeStepSectionClassName,
    getHdeStepTitleClassName,
} from '../hde-option-styles';
import { HdeRegistrationMarketingConsent } from '../HdeRegistrationMarketingConsent';
import { HDE_MARKETING_CONSENT_ERROR_MESSAGE } from '~/modules/hde/constants';
import {
    getHdeFullNameValidationError,
    isHdeFullNameValid,
} from '~/modules/hde/utils/hde-name-validation';

const EMAIL_REGEX = /[^@]+@[^.]+\..+/;
/** Allows URLs with or without https:// prefix (e.g. example.com, https://example.com). */
const WEBSITE_URL_REGEX = /^(https?:\/\/)?.+\..+$/;

interface CompanyNameWebsiteData {
    fullName?: string;
    companyEmail?: string;
    companyName: string;
    companyWebsite: string;
    user?: CurrentMemberFragment | null;
}

export function CompanyNameWebsiteStep({
    setIsValid,
    onDataChange,
    initialData,
}: StepContentProps) {
    const data = initialData as CompanyNameWebsiteData | undefined;
    const defaultFullName = useMemo(() => {
        const firstName = data?.user?.FirstName?.trim() ?? '';
        const lastName = data?.user?.LastName?.trim() ?? '';

        return [firstName, lastName].filter(Boolean).join(' ');
    }, [data?.user?.FirstName, data?.user?.LastName]);

    const [fullName, setFullName] = useState(data?.fullName ?? defaultFullName);
    const [companyEmail, setCompanyEmail] = useState(
        data?.companyEmail ?? data?.user?.Email ?? '',
    );
    const [companyName, setCompanyName] = useState(data?.companyName ?? '');
    const [companyWebsite, setCompanyWebsite] = useState(
        data?.companyWebsite ?? '',
    );
    const [marketingConsentChecked, setMarketingConsentChecked] =
        useState(false);

    const fullNameTrimmed = fullName.trim();
    const fullNameError =
        fullNameTrimmed.length > 0
            ? getHdeFullNameValidationError(fullName)
            : null;
    const isFullNameValid = isHdeFullNameValid(fullName);
    const isCompanyEmailValid =
        companyEmail.trim().length > 0 && EMAIL_REGEX.test(companyEmail.trim());
    const isCompanyNameValid =
        companyName.trim().length >= 3 && companyName.trim().length <= 50;
    const isCompanyWebsiteValid =
        companyWebsite.trim().length >= 3 &&
        companyWebsite.trim().length <= 50 &&
        WEBSITE_URL_REGEX.test(companyWebsite.trim());

    const fieldsValid =
        isFullNameValid &&
        isCompanyEmailValid &&
        isCompanyNameValid &&
        isCompanyWebsiteValid;

    const normalizeWebsiteUrl = (url: string): string => {
        const trimmed = url.trim();
        if (!trimmed) return trimmed;
        const protocolMatch = trimmed.match(/^(https?:\/\/)/i);
        const protocol = protocolMatch?.[1];
        if (protocol) {
            return protocol.toLowerCase() + trimmed.slice(protocol.length);
        }
        return `https://${trimmed}`;
    };

    useEffect(() => {
        setIsValid(fieldsValid);
        onDataChange?.({
            fullName: fullName.trim(),
            companyEmail: companyEmail.trim(),
            companyName: companyName.trim(),
            companyWebsite: normalizeWebsiteUrl(companyWebsite),
            _hdeCompanyMarketingConsent: marketingConsentChecked,
        });
    }, [
        fullName,
        companyEmail,
        companyName,
        companyWebsite,
        fieldsValid,
        marketingConsentChecked,
        setIsValid,
        onDataChange,
    ]);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <StepContent>
                <div className={getHdeStepSectionClassName()}>
                    <h2 className={getHdeStepTitleClassName()}>
                        Please enter your details
                    </h2>
                    <div className="flex max-h-[55vh] flex-col gap-2 overflow-auto md:h-150 md:max-h-none md:gap-9">
                        <div className="relative space-y-1">
                            <label
                                className={getHdeFieldLabelClassName()}
                                htmlFor="full-name"
                            >
                                Your full name *
                            </label>
                            <div className="relative">
                                <Input
                                    id="full-name"
                                    type="text"
                                    value={fullName}
                                    onChange={(e) =>
                                        setFullName(e.target.value)
                                    }
                                    placeholder="Full name"
                                    className={getHdeInputClassName()}
                                    aria-invalid={
                                        fullNameTrimmed.length > 0 &&
                                        !isFullNameValid
                                    }
                                    aria-describedby={
                                        fullNameTrimmed.length > 0 &&
                                        fullNameError
                                            ? 'full-name-error'
                                            : undefined
                                    }
                                />
                                {fullNameTrimmed.length > 0 &&
                                    fullNameError && (
                                        <p
                                            id="full-name-error"
                                            className="left-0 top-full mt-1 text-sm leading-tight text-red-600 md:absolute"
                                            role="alert"
                                        >
                                            {fullNameError}
                                        </p>
                                    )}
                            </div>
                        </div>

                        <div className="relative space-y-1">
                            <label
                                className={getHdeFieldLabelClassName()}
                                htmlFor="company-email"
                            >
                                Your business email *
                            </label>
                            <div className="relative">
                                <Input
                                    id="company-email"
                                    type="email"
                                    value={companyEmail}
                                    onChange={(e) =>
                                        setCompanyEmail(e.target.value)
                                    }
                                    placeholder="your@email.com"
                                    className={getHdeInputClassName()}
                                    aria-invalid={
                                        companyEmail.length > 0 &&
                                        !isCompanyEmailValid
                                    }
                                    aria-describedby={
                                        companyEmail.length > 0 &&
                                        !isCompanyEmailValid
                                            ? 'company-email-error'
                                            : undefined
                                    }
                                />
                                {companyEmail.length > 0 &&
                                    !isCompanyEmailValid && (
                                        <p
                                            id="company-email-error"
                                            className="left-0 top-full mt-1 text-sm leading-tight text-red-600 md:absolute"
                                            role="alert"
                                        >
                                            Please enter a valid email address.
                                        </p>
                                    )}
                            </div>
                        </div>

                        <div className="relative space-y-1">
                            <label
                                className={getHdeFieldLabelClassName()}
                                htmlFor="company-name"
                            >
                                Your company name *
                            </label>
                            <div className="relative">
                                <Input
                                    id="company-name"
                                    type="text"
                                    value={companyName}
                                    onChange={(e) =>
                                        setCompanyName(e.target.value)
                                    }
                                    placeholder="Enter your company name"
                                    className={getHdeInputClassName()}
                                    aria-invalid={
                                        companyName.length > 0 &&
                                        !isCompanyNameValid
                                    }
                                    aria-describedby={
                                        companyName.length > 0 &&
                                        !isCompanyNameValid
                                            ? 'company-name-error'
                                            : undefined
                                    }
                                />
                                {companyName.length > 0 &&
                                    !isCompanyNameValid && (
                                        <p
                                            id="company-name-error"
                                            className="left-0 top-full mt-1 text-sm leading-[1.1] text-red-600 md:absolute"
                                            role="alert"
                                        >
                                            The name must be between 3 and 50
                                            characters
                                        </p>
                                    )}
                            </div>
                        </div>

                        <div className="relative space-y-1">
                            <label
                                className={getHdeFieldLabelClassName()}
                                htmlFor="company-website"
                            >
                                Your company website *
                            </label>
                            <div className="relative">
                                <Input
                                    id="company-website"
                                    type="text"
                                    value={companyWebsite}
                                    onChange={(e) =>
                                        setCompanyWebsite(e.target.value)
                                    }
                                    placeholder="example.com"
                                    className={getHdeInputClassName()}
                                    aria-invalid={
                                        companyWebsite.length > 0 &&
                                        !isCompanyWebsiteValid
                                    }
                                    aria-describedby={
                                        companyWebsite.length > 0 &&
                                        !isCompanyWebsiteValid
                                            ? 'company-website-error'
                                            : undefined
                                    }
                                />
                                {companyWebsite.length > 0 &&
                                    !isCompanyWebsiteValid && (
                                        <p
                                            id="company-website-error"
                                            className="left-0 top-full mt-1 text-sm leading-tight text-red-600 md:absolute"
                                            role="alert"
                                        >
                                            Please enter a valid URL (e.g.
                                            example.com)
                                        </p>
                                    )}
                            </div>
                        </div>

                        <div className="relative md:-top-2">
                            <HdeRegistrationMarketingConsent
                                id="hde-company-details-marketing-consent"
                                checked={marketingConsentChecked}
                                onCheckedChange={setMarketingConsentChecked}
                                errorMessage={
                                    fieldsValid && !marketingConsentChecked
                                        ? HDE_MARKETING_CONSENT_ERROR_MESSAGE
                                        : undefined
                                }
                            />
                        </div>
                    </div>
                </div>
            </StepContent>
        </div>
    );
}
